import { useMutation } from '@apollo/client';

import type {
  StartAsset2VastIngestionByCountryListMutation,
  StartAsset2VastIngestionByCountryListMutationVariables,
} from '../../../../apis/graphql';
import { AlertType } from '../../../../common/Alert';
import { openToastAlert } from '../../../../common/ToastAlert/toastAlert';
import { START_ASSET2_VAST_INGESTION_BY_COUNTRY_LIST } from './mutations/startMultiCountryAssetIngestion';

type StartMultiCountryAssetIngestionProps = {
  countries: string[];
  creativeLibraryId?: string | null;
  creativeId?: string;
  vastUrl?: string;
};

export type UseMultiCountryAssetIngestion = {
  startMultiCountryAssetIngestion: (props: StartMultiCountryAssetIngestionProps) => Promise<void>;
};

const ERROR_MESSAGES = {
  INGESTION_FAILED: 'Ingestion Failed',
  INGESTION_STARTED: 'Ingestion Started',
  SAVE_AD_FIRST: 'Please save the ad before beginning ingestion.',
  ENTER_VAST_URL: 'Please enter a URL for the Vast Creative.',
  NO_COUNTRIES: 'No countries provided',
  UNKNOWN: 'Unknown error encountered.',
} as const;

const showIngestionError = (description?: string | null): void => {
  openToastAlert({
    alertType: AlertType.ERROR,
    message: ERROR_MESSAGES.INGESTION_FAILED,
    description: description ?? ERROR_MESSAGES.UNKNOWN,
  });
};

const showIngestionSuccess = (): void => {
  openToastAlert({
    alertType: AlertType.SUCCESS,
    message: ERROR_MESSAGES.INGESTION_STARTED,
  });
};

export const useMultiCountryAssetIngestion = (): UseMultiCountryAssetIngestion => {
  const [startAsset2VastIngestionByCountryList] = useMutation<
    StartAsset2VastIngestionByCountryListMutation,
    StartAsset2VastIngestionByCountryListMutationVariables
  >(START_ASSET2_VAST_INGESTION_BY_COUNTRY_LIST);

  const startMultiCountryAssetIngestion = async ({
    countries,
    creativeLibraryId,
    creativeId,
    vastUrl,
  }: StartMultiCountryAssetIngestionProps): Promise<void> => {
    if (!creativeLibraryId || !creativeId) {
      showIngestionError(ERROR_MESSAGES.SAVE_AD_FIRST);
      return;
    }

    if (!vastUrl) {
      showIngestionError(ERROR_MESSAGES.ENTER_VAST_URL);
      return;
    }

    if (!countries.length) {
      showIngestionError(ERROR_MESSAGES.NO_COUNTRIES);
      return;
    }

    try {
      const { data, errors } = await startAsset2VastIngestionByCountryList({
        variables: { creativeLibraryId, creativeId, vastUrl, countries },
      });

      if (errors?.length) {
        showIngestionError(errors[0].message);
        return;
      }

      const result = data?.startAsset2VastIngestionByCountryList;

      if (result?.failure?.length) {
        showIngestionError(result.failure[0]?.message);
        return;
      }

      if (result?.success?.length) {
        showIngestionSuccess();
      }
    } catch (err) {
      showIngestionError(err instanceof Error ? err.message : null);
    }
  };

  return { startMultiCountryAssetIngestion };
};
